<template>
  <div>
    <div class='flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-end'>
      <div v-if='slots.title || slots.description'>
        <h2 class='text-jananavy-900 font-medium'><slot name='title' /></h2>
        <p class='font-light text-sm'><slot name='description' /></p>
      </div>
      <ReportKey v-if='showKey' />
    </div>
    <LoadingLayout :isLoading='isLoading' class='mt-8' style='padding-top: 1px;' :slownessThreshold='0'>
      <ReportNoDataWarning :warn='!view'>
        <template v-if='props.condensed'>
          <template v-for='group in view'>
            <div class='max-w-[15rem]' v-if='group.header.id === "all"'>
              <Pie :data='view[0].pie' :options='pieOptions' />
              <h3 class='mt-4'><span class='px-3 py-1 rounded-md border inline-block text-sm text-jananavy-900 font-medium'>All Themes</span></h3>
            </div>
          </template>
        </template>
        <template v-else>
          <div class='flex flex-wrap -mx-4 -my-8'>
            <div class='px-4 py-8 w-1/2 sm:w-1/3' v-for='group in view'>
              <div class='max-w-[15rem]'>
                <Pie :data='group.pie' :options='pieOptions' />
              </div>
              <h3 class='mt-4'><span class='px-3 py-1 rounded-md border inline-block text-sm text-jananavy-900 font-medium'>{{ group.header.name }}</span></h3>
            </div>
          </div>
        </template>
      </ReportNoDataWarning>
    </LoadingLayout>
  </div>
</template>

<script setup>

import { computed, watch, useSlots } from 'vue';

import Chart from 'chart.js/auto';
import { Pie } from 'vue-chartjs';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ReportNoDataWarning from '@/components/report/report-no-data-warning.vue';

import ReportKey from '@/components/report/report-key.vue';

import { scoreMap } from '@/kernel/scoring';
import { usePayloadRequest } from '@/kernel/api';

const props = defineProps({
  params: Object,
  condensed: Boolean,
  showKey: Boolean
});

const slots = useSlots();

const request = usePayloadRequest('GET', '/assessment-response/historical-aggregate');

const pieOptions = {
  layout: {
    padding: {
      left: 0
    }
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const datapoints = context.dataset.data;
          const total = datapoints.reduce((total, datapoint) => total + datapoint, 0);
          const percentage = context.raw / total * 100;
          return percentage.toFixed(2) + '%';
        }
      }
    }
  }
}

const isLoading = computed(() => {
  return request.isLoading() || request.getData() === null;
});

const view = computed(() => {
  if (!props.params) {
    return null;
  }

  const data = request.getData();

  if (!data?.length) {
    return null;
  }

  const groups = data.filter(group => group.years[props.params.year.to]);

  if (!groups.length) {
    return null;
  }

  return groups.map((group) => {
    const scores = group.years[props.params.year.to]?.scores;

    return {
      ...group,
      pie: {
        labels: Object.keys(scores).map(key => scoreMap.getLabel(key)),
        datasets: [{
          backgroundColor: Object.keys(scores).map(key => scoreMap.getColour(key, 'hex')),
          data: Object.values(scores)
        }]
      }
    }
  });

});

const load = () => {
  if (!props.params) {
    return;
  }

  request.doRequest({
    params: {
      dimension: 'theme',
      ...props.params
    }
  });
}

watch(() => props.params, () => {
  load();
});

load();

</script>
